import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  GetChemicals,
  DeleteChemical,
} from "../../../services/GeneralMaintenanceService";
import CommonLoader from "../../common/CommonLoader";
import ViewChemical from "./ViewChemical";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import AddEditChemicalDialog from "./AddEditChemicalDialog"; // Import the AddEditChemicalDialog component
import { NotificationManager } from "react-notifications";

const ChemicalsTable = () => {
  const columns = [
    "Chemical Name",
    "Date Received",
    "Reorder Quantity",
    "Container Name",
    "Inventory Quantity",
    "Actions",
  ];
  const columnHeaders = [
    "chemicalName",
    "dateReceived",
    "reorderQuantity",
    "containerName",
    "inventoryQuantity",
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [chemicalsData, setChemicalsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedChemical, setSelectedChemical] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [isAddEditDialogOpen, setIsAddEditDialogOpen] = useState(false);

  useEffect(() => {
    const fetchChemicals = async () => {
      try {
        setLoading(true);
        const response = await GetChemicals();
        setChemicalsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching chemicals:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChemicals();
  }, []);

  useEffect(() => {
    const filteredResults = chemicalsData.filter((chemical) =>
      columnHeaders.some((header) => {
        const value = chemical[header];

        if (header === "dateReceived" && value) {
          return new Date(value)
            .toLocaleDateString("en-US")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }

        return value
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      })
    );

    setFilteredData(filteredResults);
  }, [searchTerm, chemicalsData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (chemical) => {
    setSelectedChemical(chemical);
  };

  const handleBackClick = () => {
    setSelectedChemical(null);
    setSearchTerm("");
  };

  const handleAddChemicalClick = () => {
    setSelectedChemical(null);
    setIsAddEditDialogOpen(true);
  };

  const handleEditChemicalClick = (chemical) => {
    setSelectedChemical(chemical);
    setIsAddEditDialogOpen(true);
  };

  const handleDeleteClick = (chemical) => {
    setSelectedItemToDelete(chemical);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteChemical(selectedItemToDelete.chemicalId);
      const updatedChemicals = chemicalsData.filter(
        (item) => item.chemicalId !== selectedItemToDelete.chemicalId
      );
      setChemicalsData(updatedChemicals);
      setFilteredData(updatedChemicals);
      NotificationManager.success("Chemical deleted successfully");
    } catch (error) {
      console.error("Error deleting chemical:", error);
      NotificationManager.error("Error deleting chemical");
    } finally {
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleAddEditDialogClose = () => {
    setIsAddEditDialogOpen(false);
    setSelectedChemical(null);
  };

  const handleAddEditDialogSuccess = () => {
    setIsAddEditDialogOpen(false);
    setSelectedChemical(null);
    // Reload the chemicals list after successful save/update
    const fetchChemicals = async () => {
      try {
        setLoading(true);
        const response = await GetChemicals();
        setChemicalsData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching chemicals:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchChemicals();
  };

  const renderActions = (chemical) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditChemicalClick(chemical)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(chemical)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(chemical)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
    <span className="title">Chemical Inventory </span>
    <hr />
      {selectedChemical && !isAddEditDialogOpen ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewChemical chemical={selectedChemical} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddChemicalClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((chemical, index) => (
                      <TableRow
                        key={chemical.chemicalId}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>
                            {column === "dateReceived"
                              ? new Date(chemical[column]).toLocaleDateString(
                                  "en-US"
                                )
                              : chemical[column]}
                          </TableCell>
                        ))}
                        <TableCell>{renderActions(chemical)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation"
            message="Are you sure you want to delete this chemical?"
            color="red"
          />
        </>
      )}

      {isAddEditDialogOpen && (
        <AddEditChemicalDialog
          open={isAddEditDialogOpen}
          onClose={handleAddEditDialogClose}
          onSave={handleAddEditDialogSuccess}
          selectedChemical={selectedChemical}
        />
      )}
    </>
  );
};

export default ChemicalsTable;
